import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Loader2, Search, ShieldAlert, RotateCcw, RefreshCw } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { adminLookupOrder, adminListRefunds, adminRefundOnvo, adminVerifyRefund } from "@/lib/refunds.functions";
import { isRefundable, refundAmount } from "@/lib/refund-math";
import { formatCurrency } from "@/lib/money";
import { PAYMENT_ENVIRONMENT } from "@/lib/payment-provider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

export const Route = createFileRoute("/admin/refunds")({
  head: () => ({ meta: [{ title: "Admin · Reembolsos — ZYNTRA" }, { name: "robots", content: "noindex" }] }),
  validateSearch: (s: Record<string, unknown>) => ({ q: typeof s.q === "string" ? s.q : undefined }),
  component: AdminRefunds,
});

async function withAuth<T>(fn: (h: any) => Promise<T>): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  return fn({ authorization: token ? `Bearer ${token}` : "" });
}

const STATUS: Record<string, { label: string; cls: string }> = {
  succeeded: { label: "Reembolsado", cls: "border-success/40 bg-success/10 text-success" },
  failed: { label: "Fallido", cls: "border-destructive/40 bg-destructive/10 text-destructive" },
  unknown: { label: "Sin confirmar", cls: "border-warning/40 bg-warning/10 text-warning" },
  pending: { label: "En curso", cls: "border-primary/40 bg-primary/10 text-primary" },
};
function Badge({ s }: { s: string }) {
  const m = STATUS[s] ?? { label: s, cls: "border-border bg-secondary text-secondary-foreground" };
  return <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${m.cls}`}>{m.label}</span>;
}

function AdminRefunds() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [query, setQuery] = useState(search.q ?? "");
  const [orders, setOrders] = useState<any[]>([]);
  const [history, setHistory] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate({ to: "/login" }); return; }
    supabase.from("user_roles").select("role").eq("user_id", user.id).eq("role", "admin").maybeSingle()
      .then(({ data }) => { setIsAdmin(!!data); if (data) { loadHistory(); if (search.q) lookup(search.q); } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  async function loadHistory() {
    try { const r = await withAuth((h) => adminListRefunds({ data: {}, headers: h } as any)); setHistory(r.refunds); } catch (e) { console.error(e); }
  }
  async function lookup(q = query) {
    if (q.trim().length < 3) return;
    setBusy(true); setMsg(null);
    try {
      const r = await withAuth((h) => adminLookupOrder({ data: { query: q.trim() }, headers: h } as any));
      setOrders(r.orders);
      if (!r.orders.length) setMsg({ ok: false, text: "No se encontraron órdenes." });
    } catch (e) { setMsg({ ok: false, text: (e as Error).message }); }
    finally { setBusy(false); }
  }
  async function verify(id: string) {
    try {
      const r: any = await withAuth((h) => adminVerifyRefund({ data: { refundId: id }, headers: h } as any));
      setMsg({ ok: r.status === "succeeded", text: r.note ?? `Estado: ${STATUS[r.status]?.label ?? r.status}` });
      await loadHistory(); if (orders.length) await lookup();
    } catch (e) { setMsg({ ok: false, text: (e as Error).message }); }
  }

  if (isAdmin === null) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!isAdmin) return <div className="container mx-auto max-w-xl px-4 py-16 text-center"><ShieldAlert className="mx-auto h-10 w-10 text-destructive" /><p className="mt-3">Solo administradores.</p></div>;

  return (
    <main className="container mx-auto max-w-5xl px-4 py-8">
      <Link to="/dashboard/admin" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Panel admin</Link>
      <h1 className="text-2xl font-bold">Reembolsos ONVO</h1>
      <p className="mt-1 text-sm text-muted-foreground">Entorno: {PAYMENT_ENVIRONMENT === "live" ? "real" : "prueba"}. Se devuelve el total pagado por cada entrada elegida (entrada + cargo).</p>

      <form className="mt-6 flex gap-2" onSubmit={(e) => { e.preventDefault(); lookup(); }}>
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Id de orden, id de entrada o email del comprador" />
        <Button type="submit" disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Buscar</Button>
      </form>

      {msg && <div className={`mt-4 rounded-md border p-3 text-sm ${msg.ok ? "border-success/40 bg-success/10" : "border-destructive/40 bg-destructive/10"}`}>{msg.text}</div>}

      <div className="mt-6 space-y-6">
        {orders.map((o) => <OrderCard key={o.session_id} order={o} onDone={async (m) => { setMsg(m); await lookup(); await loadHistory(); }} onVerify={verify} />)}
      </div>

      <section className="mt-10">
        <div className="flex items-center justify-between"><h2 className="text-lg font-semibold">Historial</h2>
          <Button variant="ghost" size="sm" onClick={loadHistory}><RefreshCw className="h-4 w-4" /></Button></div>
        <div className="mt-3 overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left text-xs text-muted-foreground"><tr>
              <th className="p-2">Fecha</th><th className="p-2">Evento</th><th className="p-2">Monto</th><th className="p-2">Entradas</th><th className="p-2">Motivo</th><th className="p-2">Estado</th><th className="p-2" /></tr></thead>
            <tbody>
              {history.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-muted-foreground">Sin reembolsos todavía.</td></tr>}
              {history.map((r) => (
                <tr key={r.id} className="border-t border-border align-top">
                  <td className="p-2 whitespace-nowrap">{new Date(r.requested_at).toLocaleString("es-CR")}</td>
                  <td className="p-2">{r.events?.title ?? "—"}</td>
                  <td className="p-2 whitespace-nowrap">{formatCurrency(Number(r.amount), r.currency)}</td>
                  <td className="p-2">{r.ticket_ids?.length}</td>
                  <td className="p-2 max-w-[220px]">{r.reason}{r.error_message && <div className="text-xs text-destructive">{r.error_message}</div>}</td>
                  <td className="p-2"><Badge s={r.status} /></td>
                  <td className="p-2">{(r.status === "unknown" || r.status === "pending") && <Button size="sm" variant="outline" onClick={() => verify(r.id)}>Verificar</Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

function OrderCard({ order, onDone, onVerify }: { order: any; onDone: (m: { ok: boolean; text: string }) => void; onVerify: (id: string) => void }) {
  const [sel, setSel] = useState<string[]>([]);
  const [reason, setReason] = useState("");
  const [force, setForce] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const currency = order.settlement?.currency ?? "CRC";
  const refundable = order.tickets.filter((t: any) => isRefundable(t, force));
  const amount = refundAmount(order.tickets, sel);
  const open = order.refunds.find((r: any) => r.status === "pending" || r.status === "unknown");

  async function submit() {
    setBusy(true);
    try {
      const r: any = await withAuth((h) => adminRefundOnvo({ data: { sessionId: order.session_id, ticketIds: sel, reason, force, environment: PAYMENT_ENVIRONMENT }, headers: h } as any));
      onDone(r.ok ? { ok: true, text: `Reembolso hecho: ${formatCurrency(r.amount, r.currency)}.` } : { ok: false, text: r.error ?? "No se pudo reembolsar." });
      setSel([]); setReason(""); setConfirming(false);
    } catch (e) { onDone({ ok: false, text: (e as Error).message }); }
    finally { setBusy(false); }
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><div className="font-semibold">{order.tickets[0]?.events?.title ?? "Evento"}</div>
          <div className="font-mono text-xs text-muted-foreground">{order.session_id}</div></div>
        {order.settlement && <div className="text-xs text-muted-foreground">Liquidación: {order.settlement.status}
          {Number(order.settlement.refunded_amount) > 0 && ` · reembolsado ${formatCurrency(Number(order.settlement.refunded_amount), currency)}`}
          {order.settlement.refund_adjustment_pending && <span className="ml-1 text-warning">· ajuste pendiente con el organizador</span>}</div>}
      </div>

      {open && <div className="mt-3 flex items-center justify-between rounded-md border border-warning/40 bg-warning/10 p-2 text-sm">
        Hay un reembolso sin confirmar. Verificalo antes de intentar otro.
        <Button size="sm" variant="outline" onClick={() => onVerify(open.id)}>Verificar en ONVO</Button></div>}

      <ul className="mt-3 divide-y divide-border">
        {order.tickets.map((t: any) => {
          const ok = isRefundable(t, force);
          return (
            <li key={t.id} className="flex items-center gap-3 py-2 text-sm">
              <Checkbox disabled={!ok || !!open} checked={sel.includes(t.id)} onCheckedChange={(c) => setSel((s) => c ? [...s, t.id] : s.filter((x) => x !== t.id))} />
              <span className="flex-1">{t.ticket_label ?? `Entrada ${t.id.slice(0, 8)}`}</span>
              <span className="text-xs text-muted-foreground">{t.status === "refunded" ? "Reembolsada" : t.status === "used" ? "Usada" : t.status === "active" ? "Activa" : t.status}</span>
              <span className="w-28 text-right">{formatCurrency(Number(t.price), currency)}</span>
            </li>
          );
        })}
      </ul>

      {refundable.length > 0 && !open && (
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setSel(refundable.map((t: any) => t.id))}>Elegir todas</Button>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={force} onCheckedChange={(c) => { setForce(!!c); setSel([]); }} /> Forzar entradas ya usadas</label>
          </div>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo del reembolso (obligatorio, queda registrado)" maxLength={500} />
          {!confirming ? (
            <Button disabled={!sel.length || reason.trim().length < 5} onClick={() => setConfirming(true)}><RotateCcw className="h-4 w-4" /> Reembolsar {sel.length ? formatCurrency(amount, currency) : ""}</Button>
          ) : (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
              Vas a devolver <b>{formatCurrency(amount, currency)}</b> por {sel.length} entrada(s). Las entradas quedan anuladas y vuelven a la venta. No se puede deshacer.
              <div className="mt-2 flex gap-2"><Button variant="destructive" size="sm" disabled={busy} onClick={submit}>{busy && <Loader2 className="h-4 w-4 animate-spin" />} Confirmar reembolso</Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>Cancelar</Button></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
